import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { meQueryOptions, memberProfileQueryOptions } from '@/features/account/api/queries'
import { plansQueryOptions, subscriptionMeQueryOptions } from '@/features/subscriptions/api/queries'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The `/profile` loader.
 *
 * MAIN, all three awaited: `me` (plan, name for the tier badge), `profile`
 * (full name, phone, email for the details card) and `subscriptionMe` (the
 * Plan row's "Active until" for a paid member — `me`'s own `plan` enum names
 * the tier but was never extended to carry a date, so this is the one place
 * that date exists). The hub's own card and every settings row's summary
 * value read one of the three, so none can be secondary without the Plan row
 * drawing its tier first and its date a beat later.
 *
 * `me` is very often already in cache by the time this runs: `ProfileMenu`
 * and `SidebarMemberCard` read it from `AppShell`, which mounts alongside
 * every `_app` route, and its `staleTime: Infinity` means `ensureRouteData`
 * returns that cached copy rather than asking again — see `useMe`'s own
 * comment. `profile` has no such second reader, so this is usually its one
 * request a session. `subscriptionMe`'s own 30s `staleTime` means a payment
 * just confirmed elsewhere (which invalidates `subscriptionKeys.all`) is
 * re-asked for here rather than shown stale.
 *
 * `plans` (the catalogue, for the upsell banner's "From Rs X a month") is
 * SECONDARY: it is decoration on a Free member's banner, never the reason the
 * hub itself would fail to load, and the banner already says nothing rather
 * than a wrong figure while it is still in flight (the component reads it
 * with its own `useQuery(plansQueryOptions())`, `enabled` only for a Free
 * member).
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own — see the other loaders in this wave for the full reasoning.
 */
export async function loadProfile({
  context: { queryClient },
}: {
  context: RouterContext
}): Promise<void> {
  await Promise.all([
    ensureRouteData(queryClient, meQueryOptions()),
    ensureRouteData(queryClient, memberProfileQueryOptions()),
    ensureRouteData(queryClient, subscriptionMeQueryOptions()),
  ])
  prefetchRouteData(queryClient, plansQueryOptions())
}
